import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Col,
  Row,
} from "reactstrap";
import {
  IIdValuePairResponse,
  IProductCityLevelListProps,
} from "../../../interfaces/Index";
import { _ } from "../../../utils/lib";
import "./ProductCityLevelList.scss";
import ButtonComponent from "../../common/Button/ButtonComponent";
import DataTable from "../../common/DataTable/DataTable";
import {
  headerData,
  footerData,
  bodyDataAndKeyTypes,
} from "./ProductCityLevelTableConstants";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import { normalizeBulkUploadPayload } from "../../../normalizer/GlobalProductNormalizer";
import { Can } from "../../../config/Can";
import SearchableSelect from "../../common/SearchableSelect/SearchableSelect";
import {
  NEW_CITY_LEVEL_PRODUCT_PRICE_UPLOAD_URL,
  PRODUCT_BULK_UPLOAD_URL,
  PRODUCT_INVENTORY_UPLOAD_URL,
  PRODUCT_INVENTORY_UPLOAD_V2_URL,
} from "../../../config/Url";
import UploadModal from "./Modals/UploadModal/UploadModal";
import UploadStatusModal from "./Modals/UploadStatusModal/UploadStatusModal";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchProductCityLevels: any,
  selectedCityId: any
) => {
  if (val.length >= 3) {
    pageScrollPosition.current = 0;
    fetchProductCityLevels(selectedCityId, {
      searchKey: val,
      sortColumn: "title",
      sortDirection: "ASC",
    });
  }
};

const ProductCityLevelList: React.FunctionComponent<
  IProductCityLevelListProps
> = (props: IProductCityLevelListProps) => {
  let pageScrollPosition: any = useRef(props.currentListPosition);
  let selectedCityListData: any;
  const [showBatchModal, setIsShowBatchModal] = useState(false);
  const [showInventoryModal, setIsShowInventoryModal] = useState(false);
  const [showNewPricesModal, setIsShowNewPricesModal] = useState(false);
  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [productData, setData] = useState<null | any>(null);
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });
  const [batchModal, setBatchModal] = useState(false);
  const [selectedCity, setSelectedCity] = useState<IIdValuePairResponse>(
    props.selectedCity as any
  );
  const { roles } = props;
  useEffect(() => {
    const { fetchCities } = props;
    fetchCities();
  }, []);

  useEffect(() => {
    if (!selectedCity) {
      const newSelectedCity =
        props.citiesList && props.citiesList.length > 0
          ? props.citiesList[0]
          : selectedCity;
      handleSelectedCityChange(newSelectedCity);
    }
  }, [props.citiesList, selectedCity.id]);

  useEffect(() => {
    const { productsCityLevel, isProductCityLevelAddedorUpdated } = props;
    if (productsCityLevel.length === 0 || isProductCityLevelAddedorUpdated) {
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, [
    props.citiesList.length,
    props.isCityListFetched,
    selectedCity,
    props.currentLocation,
  ]);

  useEffect(() => {
    const { productsCityLevel, pagination } = props;
    if (productsCityLevel) {
      setData(productsCityLevel);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.productsCityLevel]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (props.isProductCityLevelAdded || props.isProductCityLevelUpdated) {
      const message = props.isProductCityLevelAdded
        ? "Product added successfully!"
        : "Product updated successfully";
      handleToaster(false, message);
    }
  }, [props.isProductCityLevelAdded, props.isProductCityLevelUpdated]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any, selectedCityId: any) =>
        doAPIsearch(
          value,
          pageScrollPosition,
          props.fetchProductCityLevels,
          selectedCityId
        ),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { fetchProductCityLevels } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value, selectedCity.id);

    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      fetchProductCityLevels(selectedCity.id, {
        searchKey: value,
        sortColumn: "title",
        sortDirection: "ASC",
      });
    }
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { fetchProductCityLevels } = props;
    fetchProductCityLevels(selectedCityListData && selectedCityListData.id, {
      sortColumn: "title",
      sortDirection: "ASC",
    });
    setIsSearching(false);
    setSearchTerm("");
  };

  const loadFunc = () => {
    const { fetchProductCityLevels, pagination } = props;
    if (pagination) {
      pageScrollPosition.current = window.pageYOffset;
      setLoadMoreText({
        text: "Loading...",
        isLoading: true,
      });
      fetchProductCityLevels(selectedCity.id, {
        page: pagination,
        searchKey: searchTerm,
        sortDirection: "ASC",
        sortColumn: "title",
      });
    }
  };

  const addBatchModal = () => {
    setBatchModal(!batchModal);
  };

  const handleUploadAttributes = (file: any) => {
    const { uploadBulkProductCityLevel } = props;
    setBatchModal(false);
    const normalizedPayload = normalizeBulkUploadPayload(file);
    uploadBulkProductCityLevel(normalizedPayload, PRODUCT_BULK_UPLOAD_URL);
    setIsShowBatchModal(true);
    setIsShowInventoryModal(false);
    setIsShowNewPricesModal(false);
  };

  const handleNewUploadPrices = (file: any) => {
    const { bulkUploadNewCityLevelProductPrice } = props;
    setBatchModal(false);
    const normalizedPayload = normalizeBulkUploadPayload(file);
    bulkUploadNewCityLevelProductPrice(
      normalizedPayload,
      NEW_CITY_LEVEL_PRODUCT_PRICE_UPLOAD_URL
    );
    setIsShowNewPricesModal(true);
    setIsShowBatchModal(false);
    setIsShowInventoryModal(false);
  };

  const handleUploadInventory = (file: any) => {
    setBatchModal(false);
    const { uploadInventoryProductCityLevel } = props;
    const normalizedPayload = normalizeBulkUploadPayload(file);
    uploadInventoryProductCityLevel(
      normalizedPayload,
      PRODUCT_INVENTORY_UPLOAD_V2_URL
    );
    setIsShowBatchModal(false);
    setIsShowInventoryModal(true);
    setIsShowNewPricesModal(false);
  };

  const editProduct = (e: any, i: number) => {
    const { addUpdatePageHandler } = props;
    addUpdatePageHandler(
      true,
      productData[i],
      searchTerm,
      selectedCity,
      window.pageYOffset
    );
  };

  const closeModal = () => {
    setIsShowBatchModal(false);
    setIsShowInventoryModal(false);
    setIsShowNewPricesModal(false);
    setBatchModal(false);
  };

  const handleSelectedCityChange = (selectedCity: IIdValuePairResponse) => {
    const { fetchProductCityLevels } = props;
    if (selectedCity && selectedCity.id.length) {
      fetchProductCityLevels(selectedCity.id, {
        sortColumn: "title",
        sortDirection: "ASC",
      });
    }
    setSelectedCity(selectedCity);
  };
  return (
    <div className="product-data-table-container">
      {props.isLoading && <LoaderComponent />}
      <UploadModal
        showModal={batchModal}
        roles={roles}
        closeModal={closeModal}
        handleUploadAttributes={handleUploadAttributes}
        handleUploadInventory={handleUploadInventory}
        handleUploadPrices={handleNewUploadPrices}
      />
      <UploadStatusModal
        modalHeading="Add a batch of product's attributes"
        showModal={showBatchModal}
        modalLoading={props.isProductCityLevelBulkUploading}
        isSuccess={props.isProductCityLevelBulkUploaded}
        showProgressString={
          props.isProductCityLevelBulkUploadingError ? false : true
        }
        failedRecords={props.isProductCityLevelBulkUploadingError ? "err" : ""}
        closeModalHandler={closeModal}
        error={props.error}
      />
      <UploadStatusModal
        modalHeading="Add a batch of product's inventory"
        showModal={showInventoryModal}
        modalLoading={props.isProductCityLevelInventoryUploading}
        isSuccess={props.isProductCityLevelInventoryUploaded}
        showProgressString={
          props.isProductCityLevelInventoryUploadingError ? false : true
        }
        failedRecords={
          props.isProductCityLevelInventoryUploadingError ? "err" : ""
        }
        closeModalHandler={closeModal}
        error={props.error}
      />
      <UploadStatusModal
        modalHeading="Add a batch of product's prices"
        showModal={showNewPricesModal}
        modalLoading={props.isNewCityLevelProductPricesUploading}
        isSuccess={props.isNewCityLevelProductPricesUploaded}
        showProgressString={
          props.isNewCityLevelProductPricesUploadingError ? false : true
        }
        failedRecords={
          props.isNewCityLevelProductPricesUploadingError ? "err" : ""
        }
        closeModalHandler={closeModal}
        error={props.error}
      />
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>

      {props.isLoading && <LoaderComponent />}
      <section className="relative container sticky-top">
        <section className="row">
          <div className="col-9">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img
                    src="../../../assets/images/search-icon.svg"
                    alt="close"
                  />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by product name or vendor"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div className="input-group-append">
                <SearchableSelect
                  id="title"
                  isSearchable={true}
                  options={props.citiesList}
                  onChange={handleSelectedCityChange}
                  selectedValue={selectedCity}
                />
              </div>
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              ></div>
            </InputGroup>
          </div>
          <Col id="addBatch" xs="2">
            {roles?.includes("Commando God") ||
            roles?.includes("Commercials") ||
            roles?.includes("Commerical Supervisor") ||
            roles?.includes("Caps Upload") ? (
              <ButtonComponent
                buttonClass="btn-primary-outline button-medium"
                buttonText="Add a batch"
                onClickHandler={addBatchModal}
              />
            ) : (
              <></>
            )}
          </Col>
        </section>
      </section>
      <Can I="read" a="product">
        <DataTable
          tableId="product-list"
          // handleAllRecordSelection={handleAllRecordSelection}
          // allRecordsChecked={allChecked}
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={productData}
          noSearchResultFlag={isSearch}
          // checkBoxClickHandler={handleRecordSelection}
          editRowHandler={editProduct}
        />
        {isLoadMore && (
          <div id="load-more-container">
            <ButtonComponent
              buttonText={loadMoreText.text}
              buttonClass="btn-primary-outline button-medium"
              onClickHandler={loadFunc}
              isDisable={loadMoreText.isLoading}
            />
          </div>
        )}
      </Can>
    </div>
  );
};

export default ProductCityLevelList;
